import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth, useUser } from "@clerk/clerk-react";
import InviteShareModal from "../../../../pages/invite/InviteShareModal";
import { buildChannelSharePresets } from "../../../../config/app/referralShareCopy";
import { getReferralMe } from "../../../../services/api/app/referral";
import { getUserDisplayName } from "../../../../utils/displayName";
import { resolveToolPrepShareContext } from "../../../../config/tools/toolPrepShareLandings";
import {
  twitterIcon,
  youtubeIcon,
  instaIcon,
} from "../../../../icons/assets";
import "./ToolPrepShareFriendsModal.css";

const CHANNEL_ORDER = [
  "whatsapp",
  "instagram",
  "twitter",
  "youtube",
  "email",
  "telegram",
  "sms",
];

const CHANNEL_ICONS = {
  twitter: twitterIcon,
  instagram: instaIcon,
  youtube: youtubeIcon,
};

/**
 * Post-download: share LazyStudio (tool + invite), not the output file.
 */
const ToolPrepShareFriendsModal = ({
  open = false,
  toolKey = "",
  shareTitle = "LazyStudio",
  onClose = () => {},
}) => {
  const { getToken, isSignedIn } = useAuth();
  const { user } = useUser();
  const [referralCode, setReferralCode] = useState("");
  const [planKey, setPlanKey] = useState("free");
  const [channelId, setChannelId] = useState(null);
  const [loadHint, setLoadHint] = useState("");

  const shareCtx = useMemo(
    () => resolveToolPrepShareContext({ toolKey, shareTitle }),
    [toolKey, shareTitle]
  );

  const inviterName = useMemo(
    () => getUserDisplayName(user) || "I",
    [user]
  );

  const channels = useMemo(() => {
    const presets = buildChannelSharePresets({
      inviteUrl: "https://lazystudio.ai",
      inviterName,
      planKey,
      toolLabel: shareCtx.toolLabel,
      toolBlurb: shareCtx.toolBlurb,
    });
    return CHANNEL_ORDER.map((id) => presets[id]).filter(Boolean);
  }, [inviterName, planKey, shareCtx.toolBlurb, shareCtx.toolLabel]);

  useEffect(() => {
    if (!open) {
      setChannelId(null);
      setLoadHint("");
      return undefined;
    }

    let cancelled = false;
    (async () => {
      if (!isSignedIn) {
        setReferralCode("");
        setPlanKey("free");
        setLoadHint(
          "Sign in to include your invite link so you both earn credits."
        );
        return;
      }
      try {
        const token = await getToken();
        if (!token || cancelled) return;
        const data = await getReferralMe(token);
        if (cancelled || !data) return;
        setReferralCode(data.referral_code || "");
        setPlanKey(data.plan_key || "free");
        setLoadHint("");
      } catch {
        if (!cancelled) {
          setLoadHint("Could not load your invite code — you can still share.");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getToken, isSignedIn, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !channelId) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, channelId, onClose]);

  if (!open) return null;

  if (channelId) {
    return (
      <InviteShareModal
        channelId={channelId}
        icon={CHANNEL_ICONS[channelId]}
        referralCode={referralCode}
        inviterName={inviterName}
        planKey={planKey}
        initialLandingId={shareCtx.landingId}
        lockLanding
        toolLabel={shareCtx.toolLabel}
        toolBlurb={shareCtx.toolBlurb}
        onClose={() => setChannelId(null)}
        onShared={() => {
          window.setTimeout(() => onClose(), 400);
        }}
      />
    );
  }

  return createPortal(
    <div
      className="tpd-share-friends"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tpd-share-friends-title"
    >
      <button
        type="button"
        className="tpd-share-friends__backdrop"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="tpd-share-friends__panel">
        <header className="tpd-share-friends__head">
          <div>
            <h2 id="tpd-share-friends-title">Share with friends</h2>
            <p className="tpd-share-friends__lead">
              Invite friends to try{" "}
              <strong>{shareCtx.toolLabel}</strong> on LazyStudio — not your
              photo file.
            </p>
          </div>
          <button
            type="button"
            className="tpd-share-friends__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="tpd-share-friends__encourage">
          Friends get <span>20% extra credits</span> on their first plan. You
          earn when they subscribe.
        </div>

        {loadHint ? (
          <p className="tpd-share-friends__hint">{loadHint}</p>
        ) : null}

        <p className="tpd-share-friends__label">Share via</p>
        <div className="tpd-share-friends__channels">
          {channels.map((channel) => (
            <button
              key={channel.id}
              type="button"
              className="tpd-share-friends__channel"
              onClick={() => setChannelId(channel.id)}
              title={channel.label}
            >
              {CHANNEL_ICONS[channel.id] ? (
                <img src={CHANNEL_ICONS[channel.id]} alt="" />
              ) : (
                <span className="tpd-share-friends__mark">{channel.mark}</span>
              )}
              <span>{channel.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ToolPrepShareFriendsModal;
